/**
 * МНЕНИЕ АВТОРА КАНАЛА — «Добавить мнение» (AGI Boardroom, решение Ильи 2026-11-02).
 *
 * ЧТО ЭТО. Автор канала пишет своё мнение о посте простым текстом. Сервис генерации его чистит —
 * грамматика, пунктуация, слова-паразиты, нормы знаков канала, — но оставляет его слова, его
 * «я», его факты и его конструкции, и вставляет в пост без пометки там, где мнение уместно: его
 * позиция побеждает рассуждение агента. Мнение хранится ОТДЕЛЬНО от версий (таблица
 * `author_opinion`, миграция 0028) и переносится в каждую следующую переделку дословно. Живое
 * мнение у черновика одно: новое замещает прежнее.
 *
 * КОНТРАКТ ТРЁХ СТОРОН — этот файл, и больше ничего:
 *   интерфейс → `POST …/versions/:n/author-opinion {text}` (`AuthorOpinionBodySchema`) или
 *               `…/author-opinion/remove {}`; оба отвечают формой «переделка заказана»;
 *   бэкенд    → решение `revise` и переделка с просьбой `opinion:insert` / `opinion:remove`
 *               (`AUTHOR_OPINION_PROMPT_PREFIX`) одной транзакцией со строкой `author_opinion`;
 *   писатель  → вызов очистки (`AuthorOpinionCleanupSchema`), доводка до неподвижной точки,
 *               вставка на место `AUTHOR_OPINION_PLACEHOLDER`; версия несёт
 *               `author_opinion {opinion_id, cleaned}` (`IntakeDraftBodySchema`);
 *   бэкенд    → `authorOpinionSurvives` по каноническому тексту версии — строка становится
 *               `active`, без него версия отбивается `author_opinion_lost`.
 *
 * ИМЯ. Ничего не называется голым `opinion`: это снятый код формата («Мнение (снят)»), и он
 * ещё живёт в писателе и в подписях интерфейса. Везде — `author_opinion`.
 */

import * as z from 'zod';
import { canonicalizeFormattedText } from '../canonical/canonicalize.ts';
import { UUID_V7_RE } from '../ids.ts';
import { ISO_UTC_MS } from '../time.ts';
import { codePointLen, isWellFormedText } from '../units.ts';

const Uuid = z.string().regex(UUID_V7_RE);

/**
 * Потолок мнения, как его написал человек, в кодпоинтах. Один на всех: CHECK
 * `author_opinion.raw_text` (0028), счётчик в диалоге и верх `generation.author_opinion.
 * max_codepoints` конфига бренда — конфиг вправе потолок опустить, но не поднять: строку длиннее
 * база не примет, и интерфейс обещал бы то, что сохранить нельзя.
 */
export const AUTHOR_OPINION_MAX_CODEPOINTS = 2000;

/**
 * Потолок ОЧИЩЕННОГО мнения. Очистка вправе текст удлинить — знаки препинания, звёздочки
 * организаций, запрещённых в РФ, имя вместо адреса («x.ai» → словами), — но не вдвое: вдвое
 * длиннее значит переписано, а не очищено. CHECK `author_opinion.cleaned_text` (0028).
 */
export const AUTHOR_OPINION_CLEANED_MAX_CODEPOINTS = 4000;

/**
 * Длина в кодпоинтах, непустая по смыслу, без одиночных суррогатов и без U+0000. NUL — отказ
 * схемы, потому что CHECK 0028 меряет потолок `length()` SQLite, а она считает символы только до
 * первого NUL: текст, принятый здесь, база отбила бы ограничением, и маршрут ответил бы ошибкой
 * базы вместо 400 (ревью 2026-11-02).
 */
function opinionText(max: number) {
  return z.string().refine(
    (s) => {
      const n = codePointLen(s) as unknown as number;
      return n >= 1 && n <= max && s.trim() !== '' && isWellFormedText(s) && !s.includes('\u0000');
    },
    { error: `ожидался текст 1..${String(max)} кодпоинтов, не из одних пробелов и без U+0000` },
  );
}

/* ─────────────────────────── просьба переделки ─────────────────────────── */

/**
 * ПРИСТАВКА ПРОСЬБЫ ПЕРЕДЕЛКИ — тот же приём, что у обложки (`poster:`, `api/poster.ts`):
 * `rework_request` знает только просьбу и цели, и заводить ради мнения колонку значило бы
 * перестраивать таблицу, которую оно по смыслу не меняет. Цель переделки — `['text']`.
 *
 * Приставка — НЕ полномочие. Набрать «opinion:…» в «Переделать…» может любой, поэтому
 * человеческую просьбу с этой приставкой бэкенд отбивает, а писатель входит в режим мнения
 * только когда живая строка мнения называет ЭТУ переделку (`IntakeAuthorOpinionSchema`:
 * `rework_id` для вставки, `removal_rework_id` для снятия).
 */
export const AUTHOR_OPINION_PROMPT_PREFIX = 'opinion:';
/** Вставить мнение (новое или заменяющее прежнее). */
export const AUTHOR_OPINION_INSERT_PROMPT = `${AUTHOR_OPINION_PROMPT_PREFIX}insert`;
/** Убрать мнение из поста и закрыть место, где оно стояло. */
export const AUTHOR_OPINION_REMOVE_PROMPT = `${AUTHOR_OPINION_PROMPT_PREFIX}remove`;

export type AuthorOpinionPrompt =
  | { readonly kind: 'insert' }
  | { readonly kind: 'remove' }
  | { readonly kind: 'invalid'; readonly reason: string };

/**
 * `null` — просьба не про мнение. `invalid` — приставка зарезервирована, а действие неизвестно:
 * бэкенд такую просьбу человека отбивает, писатель её не исполняет ни в каком режиме.
 */
export function parseAuthorOpinionPrompt(prompt: string): AuthorOpinionPrompt | null {
  const p = prompt.trim();
  if (!p.startsWith(AUTHOR_OPINION_PROMPT_PREFIX)) return null;
  if (p === AUTHOR_OPINION_INSERT_PROMPT) return { kind: 'insert' };
  if (p === AUTHOR_OPINION_REMOVE_PROMPT) return { kind: 'remove' };
  return {
    kind: 'invalid',
    reason:
      `приставка «${AUTHOR_OPINION_PROMPT_PREFIX}» зарезервирована за мнением автора, ` +
      `а просьба ${JSON.stringify(p)} — ни вставка, ни снятие`,
  };
}

/**
 * Место мнения в тексте переделки. Модель ставит токен ОТДЕЛЬНЫМ абзацем там, где мнению место,
 * и правит только своё рассуждение; код до разбора разметки вклеивает очищенный текст на место
 * строки токена. Так модель не перепечатывает слова автора никогда: перепечатка — это шанс
 * исправить в них запятую, а исправленное мнение — уже не его.
 *
 * Без символов диалекта разметки и без кавычек — поэтому дефис, а не подчёркивание: `_` в
 * диалекте — маркер курсива. Модель, знающая диалект, экранировала бы его (`AUTHOR\_OPINION`),
 * и вклейка по точному совпадению токена не нашла бы; неэкранированный, он открыл бы курсив, и
 * разбор отказал бы. Дефис диалекту не принадлежит: `entitiesToSource` его не экранирует,
 * разбор оставляет как есть, нормализация вывода модели не трогает.
 */
export const AUTHOR_OPINION_PLACEHOLDER = '{{AUTHOR-OPINION}}';

/* ─────────────────────────── состояния строки ─────────────────────────── */

/**
 * Жизнь строки `author_opinion` (CHECK и триггер переходов — миграция 0028):
 *   · `pending`    — заказана вставка, очищенного текста ещё нет;
 *   · `active`     — версия писателя несёт очищенный текст; переносится в каждую переделку;
 *   · `superseded` — его заменило новое мнение;
 *   · `detached`   — человек правкой убрал его из текста; дальше не переносится;
 *   · `removed`    — снято по просьбе «Убрать мнение»;
 *   · `cancelled`  — вставка не состоялась: черновик удалён, переделка провалилась, версию
 *                    выпустил человек.
 */
export const AUTHOR_OPINION_STATES = [
  'pending',
  'active',
  'superseded',
  'detached',
  'removed',
  'cancelled',
] as const;
export type AuthorOpinionState = (typeof AUTHOR_OPINION_STATES)[number];

/** Живые состояния: у черновика не больше одной такой строки (частичный UNIQUE, 0028). */
export const AUTHOR_OPINION_LIVE_STATES = ['pending', 'active'] as const;
export type AuthorOpinionLiveState = (typeof AUTHOR_OPINION_LIVE_STATES)[number];

/* ─────────────────────────── провод интерфейса ─────────────────────────── */

/** Тело `POST /api/v1/drafts/:id/versions/:n/author-opinion` — мнение как его написал человек. */
export const AuthorOpinionBodySchema = z.strictObject({
  text: opinionText(AUTHOR_OPINION_MAX_CODEPOINTS),
});
export type AuthorOpinionBody = z.output<typeof AuthorOpinionBodySchema>;

/** Тело `POST /api/v1/drafts/:id/versions/:n/author-opinion/remove` — пустое и закрытое. */
export const AuthorOpinionRemoveBodySchema = z.strictObject({});

/**
 * Мнение в модели рендера черновика (`RenderModelSchema.author_opinion`): что написал человек,
 * во что его очистили, кто и когда, и есть ли очищенный текст в ЭТОЙ версии дословно.
 * `in_version: null` — очищенного текста ещё нет (`pending`), сверять нечего.
 */
export const AuthorOpinionViewSchema = z.strictObject({
  opinion_id: Uuid,
  state: z.enum(AUTHOR_OPINION_STATES),
  raw: z.string().min(1),
  cleaned: z.string().min(1).nullable(),
  submitted_by_login: z.string().min(1),
  created_at: z.string().regex(ISO_UTC_MS),
  in_version: z.boolean().nullable(),
});
export type AuthorOpinionView = z.output<typeof AuthorOpinionViewSchema>;

/* ─────────────────────────── провод писателя ─────────────────────────── */

/**
 * Живое мнение в `GET /intake/v1/drafts/:id` (верхний уровень ответа, `null` — живого нет).
 * Писатель читает его на каждой переделке: вставка — `pending` и `rework_id` этой переделки (на
 * место `replaces`, если он есть); снятие — `active` и `removal_rework_id` этой переделки; любая
 * другая переделка при `active` переносит `cleaned` дословно.
 */
export const IntakeAuthorOpinionSchema = z
  .strictObject({
    opinion_id: Uuid,
    state: z.enum(AUTHOR_OPINION_LIVE_STATES),
    raw: z.string().min(1),
    cleaned: z.string().min(1).nullable(),
    /** Переделка, заказавшая вставку этого мнения. */
    rework_id: Uuid.nullable(),
    /** Переделка, заказавшая его снятие; `null` — снятия не заказывали. */
    removal_rework_id: Uuid.nullable(),
    /**
     * Мнение, которое вставка ЭТОГО заменяет в тексте («Изменить мнение…»): id прежнего мнения и
     * его очищенный текст. Маршрут вставки закрывает прежнее живое мнение (`superseded`) раньше,
     * чем заводит новое (частичный UNIQUE), и живым в виде черновика остаётся только новое; без
     * этого поля писатель не знал бы, какой абзац версии — слова автора, и прежнее мнение осталось
     * бы в посте рядом с новым или ушло бы в рассуждение агента. Писатель ставит токен
     * (`AUTHOR_OPINION_PLACEHOLDER`) на место этого текста и вклеивает туда новое мнение; текста в
     * версии не нашлось — заменять нечего, и вставка идёт как первая.
     *
     * Бэкенд заполняет его только у `pending`: последняя строка черновика в `superseded`, чей
     * очищенный текст стоит в тексте версии, с которой заказана вставка (`authorOpinionSurvives`).
     * Так находится и мнение, чья замена провалилась (вставка `cancelled`): его текст всё ещё в
     * версии. Такой строки нет — `null`; у `active` — всегда `null`: замена уже состоялась.
     */
    replaces: z.strictObject({ opinion_id: Uuid, cleaned: z.string().min(1) }).nullable(),
  })
  .check((ctx) => {
    const v = ctx.value;
    if (v.replaces !== null && (v.state !== 'pending' || v.replaces.opinion_id === v.opinion_id)) {
      ctx.issues.push({
        code: 'custom',
        input: v,
        path: ['replaces'],
        message: 'author_opinion_replaces_mismatch',
      });
    }
    // Очищенный текст появляется ровно вместе с активацией (0028) — иного сочетания не бывает.
    if ((v.state === 'pending') !== (v.cleaned === null)) {
      ctx.issues.push({
        code: 'custom',
        input: v,
        path: ['cleaned'],
        message: 'author_opinion_cleaned_state_mismatch',
      });
    }
    if (v.state === 'pending' && v.removal_rework_id !== null) {
      ctx.issues.push({
        code: 'custom',
        input: v,
        path: ['removal_rework_id'],
        message: 'author_opinion_pending_removal',
      });
    }
  });
export type IntakeAuthorOpinion = z.output<typeof IntakeAuthorOpinionSchema>;

/**
 * Очищенный текст в канонической форме — та, что лежит в `author_opinion.cleaned_text` и
 * встречается в каноническом тексте версии дословно. Отказ схемы на неканонической форме — не
 * придирка: сверка `authorOpinionSurvives` идёт по каноническому тексту, и мнение с хвостовым
 * пробелом или CR «не нашлось» бы в посте, где оно стоит.
 */
export const AuthorOpinionCleanedText = opinionText(AUTHOR_OPINION_CLEANED_MAX_CODEPOINTS).refine(
  (s) => canonicalAuthorOpinionText(s) === s,
  { error: 'author_opinion_not_canonical' },
);

/**
 * `IntakeDraftBodySchema.author_opinion` — мнение, которое несёт эта версия. Бэкенд сверяет его со
 * строкой (`opinion_id` — её, черновика этой версии и живая) и с текстом версии.
 */
export const IntakeDraftAuthorOpinionSchema = z.strictObject({
  opinion_id: Uuid,
  cleaned: AuthorOpinionCleanedText,
});
export type IntakeDraftAuthorOpinion = z.output<typeof IntakeDraftAuthorOpinionSchema>;

/**
 * Ответ вызова очистки — строгий вывод модели. Отдельная схема, а НЕ поле `WriterOutputSchema`:
 * схема вывода писателя общая для всех брендов, и новое поле в ней стало бы заданием «заполни» в
 * каждом посте GeoBoost, чья копия `core/system.writer` его не описывает.
 */
export const AuthorOpinionCleanupSchema = z.strictObject({
  /**
   * Мнение после очистки: его слова и его порядок, без слов-паразитов, с исправленными
   * грамматикой и пунктуацией. Потолок — предохранитель от разогнавшегося ответа, а не норма
   * длины.
   *
   * Потолок — в КОДПОИНТАХ, как у CHECK `cleaned_text` (0028) и `IntakeDraftAuthorOpinionSchema`:
   * `.max()` zod считает code units UTF-16, и честная очистка мнения с эмодзи или
   * математическими буквами отбивалась бы на разборе, хотя приём версии её принял бы. В подсказку
   * модели потолок идёт `maxLength` JSON Schema — он тоже в символах (кодпоинтах); ставит его
   * `.meta`, потому что уточнение zod в JSON Schema не переводится.
   */
  cleaned: z
    .string()
    .min(1)
    .refine(
      (s) => (codePointLen(s) as unknown as number) <= AUTHOR_OPINION_CLEANED_MAX_CODEPOINTS,
      { error: `ожидалось не больше ${String(AUTHOR_OPINION_CLEANED_MAX_CODEPOINTS)} кодпоинтов` },
    )
    .meta({ maxLength: AUTHOR_OPINION_CLEANED_MAX_CODEPOINTS }),
});
export type AuthorOpinionCleanup = z.output<typeof AuthorOpinionCleanupSchema>;

/**
 * Та же схема как JSON Schema для `output_config.format` вызова очистки — тем же путём, что
 * `WRITER_OUTPUT_JSON_SCHEMA` (`api/writer.ts`): схема запроса и схема разбора ответа обязаны
 * быть одним объектом, иначе строгий вывод и разбор разъедутся молча.
 */
export const AUTHOR_OPINION_CLEANUP_JSON_SCHEMA = z.toJSONSchema(AuthorOpinionCleanupSchema, {
  target: 'draft-2020-12',
});

/* ─────────────────────────── сверка текста ─────────────────────────── */

/**
 * Текст мнения по правилам канонизации текста (шаги B и C `canonicalize`: CR, управляющие
 * символы, U+2028–U+202E, обрезка краёв). Очищенный текст хранится в этой форме, потому что в
 * этой форме он и окажется в канонической версии: вклеенный в пост отдельным абзацем, он больше
 * не меняется канонизацией ни на символ.
 */
export function canonicalAuthorOpinionText(s: string): string {
  return canonicalizeFormattedText(s, []).text;
}

/**
 * Пережило ли мнение сборку: очищенный текст стоит в каноническом тексте версии одним куском,
 * дословно. Одна функция у писателя (после вклейки, после гейта, перед отправкой) и у бэкенда
 * (приём версии) — расходиться им не в чем. Пустое мнение не «встречается» нигде.
 */
export function authorOpinionSurvives(text: string, cleaned: string): boolean {
  return cleaned.trim() !== '' && text.includes(cleaned);
}
